/**
 * LifetimeCard — the multi-year lifetime projection, rebuilt in the range
 * dashboard's design language (clean `.rd` card + hand-rolled SVG, not the
 * classic Tailwind/recharts version). Same data (`LifetimeProjection`), same
 * toggles: metric · annual/cumulative · NPV/nominal (economic only).
 *
 * The series decays year-over-year (degradation); in cumulative view a dashed
 * reference shows the naive "no-decay" total so the gap is legible.
 */

import { useMemo, useState } from "react";
import type { LifetimeProjection, YearlyProjection } from "../../lifetime/domain/types";

type Metric = "economic" | "health" | "biodiversity" | "co2e" | "generation";
type ViewMode = "annual" | "cumulative";
type Mode = "nominal" | "npv";

interface MetricSpec {
  key: Metric;
  label: string;
  unit: string;
  color: string; // CSS var
  value: (yr: YearlyProjection, mode: Mode) => number;
  fmt: (v: number) => string;
  lifetime: (p: LifetimeProjection, mode: Mode) => number;
}

const fmtUsd = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(a / 1e3).toFixed(0)}k`;
  return `$${a.toFixed(0)}`;
};
const sig = (v: number) => {
  const a = Math.abs(v);
  if (a === 0) return "0";
  if (a >= 100) return a.toFixed(0);
  if (a >= 1) return a.toFixed(1);
  return a.toPrecision(2);
};

const METRICS: MetricSpec[] = [
  {
    key: "economic", label: "Economic", unit: "USD", color: "var(--econ)",
    value: (yr, mode) =>
      mode === "npv" ? yr.economicValueNpvUsd : yr.economicValueNominalUsd,
    fmt: fmtUsd,
    lifetime: (p, mode) =>
      mode === "npv" ? p.lifetimeEconomicNpvUsd : p.lifetimeEconomicNominalUsd,
  },
  {
    key: "health", label: "Health", unit: "DALYs", color: "var(--health)",
    value: (yr) => yr.avoidedDalys, fmt: (v) => `${sig(v)}`,
    lifetime: (p) => p.lifetimeAvoidedDalys,
  },
  {
    key: "biodiversity", label: "Biodiversity", unit: "species·yr", color: "var(--eco)",
    value: (yr) => yr.avoidedPdfs, fmt: (v) => `${sig(v)}`,
    lifetime: (p) => p.lifetimeAvoidedPdfs,
  },
  {
    key: "co2e", label: "CO₂e", unit: "t", color: "var(--accent)",
    value: (yr) => yr.avoidedCo2eKg / 1000, fmt: (v) => `${sig(v)} t`,
    lifetime: (p) => p.lifetimeAvoidedCo2eTonnes,
  },
  {
    key: "generation", label: "Generation", unit: "GWh", color: "var(--fixed)",
    value: (yr) => yr.projectedGenerationMwh / 1000, fmt: (v) => `${sig(v)} GWh`,
    lifetime: (p) => p.lifetimeGenerationGwh,
  },
];

export function LifetimeCard({ projection }: { projection: LifetimeProjection }) {
  const [metric, setMetric] = useState<Metric>("economic");
  const [view, setView] = useState<ViewMode>("cumulative");
  const [mode, setMode] = useState<Mode>("npv");
  const spec = METRICS.find((m) => m.key === metric) ?? METRICS[0];

  const { pts, cumTotal, chart } = useMemo(() => {
    let cum = 0;
    const pts = projection.yearlyProjections.map((yr) => {
      const annual = Math.abs(spec.value(yr, mode));
      cum += annual;
      return { year: yr.year, annual, cum };
    });
    const series = pts.map((p) => (view === "cumulative" ? p.cum : p.annual));
    const n = series.length;
    const max = Math.max(1e-12, ...series);
    // naive no-decay reference for cumulative view: year-1 annual × N
    const refTotal = pts.length ? pts[0].annual * n : 0;
    const W = 300, H = 92, pad = 2;
    const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * (W - pad * 2) + pad);
    const y = (v: number) => H - pad - (v / max) * (H - pad * 2);
    const line = series
      .map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
      .join(" ");
    const area = `${line} L${x(n - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
    const ref =
      view === "cumulative" && refTotal > 0
        ? `M${x(0).toFixed(1)},${y(pts[0].annual).toFixed(1)} L${x(n - 1).toFixed(1)},${y(refTotal).toFixed(1)}`
        : null;
    return {
      pts,
      cumTotal: cum,
      chart: { line, area, ref, W, H, firstYear: pts[0]?.year, lastYear: pts[n - 1]?.year },
    };
  }, [projection.yearlyProjections, spec, view, mode]);

  const lifetime = spec.lifetime(projection, mode);
  const years = projection.config.projectLifetimeYears;
  const gid = `rd-lt-${metric}`;

  return (
    <div className="card lifetime" style={{ margin: 0 }}>
      <div className="ch">
        <h2>Lifetime projection</h2>
        <span className="mut">
          {years}-yr horizon · {projection.config.degradationModel} degradation
        </span>
      </div>
      <div className="cb">
        <div className="ltbar">
          <div className="seg ghost">
            {METRICS.map((m) => (
              <button
                key={m.key}
                aria-pressed={m.key === metric}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="ltbar-r">
            <div className="seg ghost">
              <button aria-pressed={view === "annual"} onClick={() => setView("annual")}>
                Annual
              </button>
              <button
                aria-pressed={view === "cumulative"}
                onClick={() => setView("cumulative")}
              >
                Cumulative
              </button>
            </div>
            {metric === "economic" && (
              <div className="seg ghost">
                <button aria-pressed={mode === "nominal"} onClick={() => setMode("nominal")}>
                  Nominal
                </button>
                <button aria-pressed={mode === "npv"} onClick={() => setMode("npv")}>
                  NPV ({(projection.config.socialDiscountRate * 100).toFixed(0)}%)
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="statrow" style={{ marginTop: 14 }}>
          <div className="s">
            <div className="n" style={{ color: spec.color }}>
              {spec.fmt(lifetime)}
              {metric !== "generation" && metric !== "co2e" && (
                <span className="u"> {spec.unit}</span>
              )}
            </div>
            <div className="k">
              lifetime {metric === "economic" ? (mode === "npv" ? "NPV" : "nominal") : "total"}
            </div>
          </div>
          <div className="s">
            <div className="n">{spec.fmt(lifetime / years)}</div>
            <div className="k">per-year avg</div>
          </div>
        </div>

        <div className="genk" style={{ marginTop: 14 }}>
          {view === "cumulative" ? "Cumulative" : "Annual"} {spec.label.toLowerCase()} ·
          year {chart.firstYear}–{chart.lastYear}
        </div>
        <svg className="spark ltchart" viewBox={`0 0 ${chart.W} ${chart.H}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={spec.color} stopOpacity=".32" />
              <stop offset="1" stopColor={spec.color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={chart.area} fill={`url(#${gid})`} />
          {chart.ref && (
            <path
              d={chart.ref}
              fill="none"
              stroke="var(--ink-3)"
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.7}
            />
          )}
          <path d={chart.line} fill="none" stroke={spec.color} strokeWidth={1.7} />
        </svg>
        <div className="mut" style={{ fontSize: 11 }}>
          {view === "cumulative" && chart.ref ? (
            <>
              Solid = modeled (degradation applied); dashed = naive constant
              year-1 output. The gap is the degradation you'd otherwise miss.
            </>
          ) : (
            <>
              Modeled per-year impact — declines as the asset degrades over its{" "}
              {years}-year life.
            </>
          )}
        </div>
      </div>
    </div>
  );
}
